import { Caption1, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, Tooltip, makeStyles, tokens } from "@fluentui/react-components";
import { Add16Regular, ChevronDown12Regular } from "@fluentui/react-icons";
import { useState } from "react";
import { ConfirmDialog } from "@/components/app-dialog";
import { canWorkspace, useEffectiveWorkspaceRole } from "@/features/access/workspace-access";
import { useCatalogStore } from "@/store/catalog-store";
import type { DocumentBlock } from "@/engine/types";
import type { DocTemplateModalIntent } from "./DocTemplateModal";
import { blocksFromOffer, docTemplateHint, type DocTemplateOffer } from "./doc-templates";

const useStyles = makeStyles({
  wrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "6px",
    marginBottom: tokens.spacingVerticalS,
  },
  label: {
    color: tokens.colorNeutralForeground3,
  },
  row: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
  },
  group: {
    display: "inline-flex",
    height: "28px",
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: "hidden",
  },
  chip: {
    height: "100%",
    padding: "0 10px",
    border: "none",
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground1,
    font: "inherit",
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":active": { backgroundColor: tokens.colorNeutralBackground1Pressed },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "-2px" },
  },
  more: {
    width: "22px",
    padding: 0,
    borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: tokens.colorNeutralForeground3,
  },
  browse: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    height: "28px",
    padding: "0 10px",
    borderRadius: tokens.borderRadiusMedium,
    border: `1px dashed ${tokens.colorNeutralStroke1}`,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground2,
    font: "inherit",
    fontSize: tokens.fontSizeBase300,
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "1px" },
  },
});

/**
 * 본문에 글자가 없을 때만. 칩을 누르면 그 목차로 본문을 바꾼다. 문서 종류는 바꾸지 않는다.
 * 칩 옆 ▾에서 수정·복사·삭제, 끝의 버튼에서 갤러리를 연다.
 */
export function DocTemplateBar({
  offers,
  onApply,
  onOpenModal,
}: {
  offers: readonly DocTemplateOffer[];
  onApply: (blocks: DocumentBlock[]) => void;
  onOpenModal: (intent: DocTemplateModalIntent) => void;
}) {
  const styles = useStyles();
  const role = useEffectiveWorkspaceRole();
  const canManage = canWorkspace(role, "workspace.settings.update");
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  if (!offers.length) return null;
  return (
    <div className={styles.wrap}>
      <Caption1 className={styles.label}>템플릿</Caption1>
      <div className={styles.row} role="group" aria-label="템플릿">
        {offers.map((offer) => (
          <span key={offer.id} className={styles.group}>
            <Tooltip content={docTemplateHint(offer)} relationship="description">
              <button type="button" className={styles.chip} onClick={() => onApply(blocksFromOffer(offer))}>
                {offer.name}
              </button>
            </Tooltip>
            <Menu positioning="below-end">
              <MenuTrigger disableButtonEnhancement>
                <button type="button" className={`${styles.chip} ${styles.more}`} aria-label={`${offer.name} 동작`}>
                  <ChevronDown12Regular />
                </button>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem onClick={() => onApply(blocksFromOffer(offer))}>적용</MenuItem>
                  <MenuItem onClick={() => onOpenModal({ type: "browse", selectId: offer.id })}>미리보기</MenuItem>
                  {canManage && offer.origin === "system" ? <MenuItem onClick={() => onOpenModal({ type: "copy", id: offer.id })}>복사하여 편집</MenuItem> : null}
                  {canManage && offer.origin === "workspace" ? (
                    <>
                      <MenuItem onClick={() => onOpenModal({ type: "edit", id: offer.id })}>수정</MenuItem>
                      <MenuItem onClick={() => onOpenModal({ type: "copy", id: offer.id })}>복사하여 편집</MenuItem>
                      <MenuItem onClick={() => setPendingDelete(offer.id)}>삭제</MenuItem>
                    </>
                  ) : null}
                </MenuList>
              </MenuPopover>
            </Menu>
          </span>
        ))}
        <button type="button" className={styles.browse} onClick={() => onOpenModal({ type: "browse" })}>
          <Add16Regular />
          템플릿 둘러보기{canManage ? " / 관리" : ""}
        </button>
      </div>
      <ConfirmDialog
        open={pendingDelete != null}
        title="템플릿을 지울까요?"
        message="이 템플릿을 지웁니다. 이미 만든 문서는 그대로입니다."
        confirmLabel="지우기"
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) useCatalogStore.getState().deleteWorkspaceDocTemplate(pendingDelete);
          setPendingDelete(null);
        }}
      />
    </div>
  );
}
